import {
  exponentsOf,
  type Poly,
  polyVariables,
} from "@/lib/tool-kit/algebra/poly";
import { absBig, BIG_ONE, gcdBig, lcmBig } from "@/lib/tool-kit/math/bigint";
import {
  addR,
  divR,
  equalsR,
  formatPolynomial,
  formatStyled,
  intR,
  isZeroR,
  mulR,
  type NumberStyle,
  negR,
  type PolynomialTerm,
  RATIONAL_ZERO,
  type Rational,
  signR,
  writesAsFraction,
} from "@/lib/tool-kit/math/rational";

/**
 * One-variable polynomials as coefficient arrays, lowest degree first:
 * 3x² − 2 is `[−2, 0, 3]`. The zero polynomial is `[]`.
 */
export type Uni = Rational[];

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

export function superscript(n: number): string {
  return String(n)
    .split("")
    .map((digit) => SUPERSCRIPTS[Number(digit)])
    .join("");
}

export function uniTrim(c: Uni): Uni {
  let end = c.length;
  while (end > 0 && isZeroR(c[end - 1])) {
    end -= 1;
  }
  return c.slice(0, end);
}

/** Degree, with the zero polynomial at −1. */
export function uniDegree(c: Uni): number {
  return uniTrim(c).length - 1;
}

export function uniCoefficient(c: Uni, degree: number): Rational {
  return c[degree] ?? RATIONAL_ZERO;
}

export function uniLeading(c: Uni): Rational {
  const t = uniTrim(c);
  return t.at(-1) ?? RATIONAL_ZERO;
}

export function uniAdd(a: Uni, b: Uni): Uni {
  const length = Math.max(a.length, b.length);
  const result: Uni = [];
  for (let i = 0; i < length; i += 1) {
    result.push(addR(uniCoefficient(a, i), uniCoefficient(b, i)));
  }
  return uniTrim(result);
}

export function uniNeg(a: Uni): Uni {
  return a.map(negR);
}

export function uniSub(a: Uni, b: Uni): Uni {
  return uniAdd(a, uniNeg(b));
}

export function uniScale(a: Uni, factor: Rational): Uni {
  return uniTrim(a.map((c) => mulR(c, factor)));
}

export function uniMul(a: Uni, b: Uni): Uni {
  if (a.length === 0 || b.length === 0) {
    return [];
  }
  const result: Uni = Array.from(
    { length: a.length + b.length - 1 },
    () => RATIONAL_ZERO
  );
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < b.length; j += 1) {
      result[i + j] = addR(result[i + j], mulR(a[i], b[j]));
    }
  }
  return uniTrim(result);
}

/** a · x^shift */
export function uniShift(a: Uni, shift: number): Uni {
  if (a.length === 0) {
    return [];
  }
  return [...Array.from({ length: shift }, () => RATIONAL_ZERO), ...a];
}

export function uniPow(a: Uni, exponent: number): Uni {
  let result: Uni = [intR(1)];
  for (let i = 0; i < exponent; i += 1) {
    result = uniMul(result, a);
  }
  return result;
}

/** Horner evaluation at a rational point. */
export function uniEval(c: Uni, x: Rational): Rational {
  let result = RATIONAL_ZERO;
  for (let i = c.length - 1; i >= 0; i -= 1) {
    result = addR(mulR(result, x), c[i]);
  }
  return result;
}

export function uniEquals(a: Uni, b: Uni): boolean {
  const x = uniTrim(a);
  const y = uniTrim(b);
  return x.length === y.length && x.every((value, i) => equalsR(value, y[i]));
}

export interface UniDivision {
  quotient: Uni;
  remainder: Uni;
}

/** Polynomial long division over the rationals. `divisor` must not be zero. */
export function uniDivmod(dividend: Uni, divisor: Uni): UniDivision {
  const d = uniTrim(divisor);
  let remainder = uniTrim(dividend);
  let quotient: Uni = [];
  const lead = d.at(-1) as Rational;
  while (remainder.length >= d.length && remainder.length > 0) {
    const shift = remainder.length - d.length;
    const factor = divR(remainder.at(-1) as Rational, lead);
    const term: Uni = uniShift([factor], shift);
    quotient = uniAdd(quotient, term);
    remainder = uniSub(remainder, uniMul(term, d));
  }
  return { quotient, remainder };
}

/** The coefficient array of a polynomial in one named variable, or null if it has others. */
export function uniFromPoly(p: Poly, variable: string): Uni | null {
  const others = polyVariables(p).filter((name) => name !== variable);
  if (others.length > 0) {
    return null;
  }
  const result: Rational[] = [];
  for (const [key, value] of Object.entries(p)) {
    const degree = exponentsOf(key)[variable] ?? 0;
    while (result.length <= degree) {
      result.push(RATIONAL_ZERO);
    }
    result[degree] = value;
  }
  return uniTrim(result);
}

export function variableText(variable: string, degree: number): string {
  if (degree === 0) {
    return "";
  }
  return degree === 1 ? variable : `${variable}${superscript(degree)}`;
}

/** "3x² − 2" in standard form (highest degree first); "0" for the zero polynomial. */
export function formatUni(
  c: Uni,
  variable: string,
  style: NumberStyle = "fraction",
  decimalMark = "."
): string {
  const terms: PolynomialTerm[] = [];
  for (let degree = c.length - 1; degree >= 0; degree -= 1) {
    terms.push({
      coefficient: c[degree],
      variable: variableText(variable, degree),
    });
  }
  return formatPolynomial(terms, style, decimalMark);
}

/** Least common multiple of every denominator in the arrays. */
export function uniCommonDenominator(parts: readonly Uni[]): bigint {
  let result = BIG_ONE;
  for (const part of parts) {
    for (const value of part) {
      result = lcmBig(result, value.den);
    }
  }
  return result;
}

/** Greatest common divisor of the numerators of integer coefficients (1 for zero). */
export function integerContent(c: Uni): bigint {
  let result = BigInt(0);
  for (const value of c) {
    result = gcdBig(result, absBig(value.num));
  }
  return result === BigInt(0) ? BIG_ONE : result;
}

export function uniSign(c: Uni): number {
  return signR(uniLeading(c));
}

const NEEDS_BRACKETS = /^−|\//;

/**
 * A polynomial with a value put in for the variable, written as a student would:
 * `3 × 9 − 2`, `2 × (−4)² + 1`. Used for the "check by substituting" step.
 */
export function substitutionText(
  c: Uni,
  value: Rational,
  style: NumberStyle = "fraction",
  decimalMark = "."
): string {
  const shown = formatStyled(value, style, decimalMark);
  const bracketed = NEEDS_BRACKETS.test(shown) ? `(${shown})` : shown;
  const parts: string[] = [];
  for (let degree = c.length - 1; degree >= 0; degree -= 1) {
    const coefficient = c[degree];
    if (isZeroR(coefficient)) {
      continue;
    }
    const magnitude = signR(coefficient) < 0 ? negR(coefficient) : coefficient;
    const body =
      degree === 0
        ? formatStyled(magnitude, style, decimalMark)
        : substitutedTerm(magnitude, bracketed, degree, style, decimalMark);
    if (parts.length === 0) {
      parts.push(signR(coefficient) < 0 ? `−${body}` : body);
    } else {
      parts.push(` ${signR(coefficient) < 0 ? "−" : "+"} ${body}`);
    }
  }
  return parts.length === 0 ? "0" : parts.join("");
}

function substitutedTerm(
  magnitude: Rational,
  shown: string,
  degree: number,
  style: NumberStyle,
  decimalMark: string
): string {
  const power = degree === 1 ? shown : `${shown}${superscript(degree)}`;
  if (equalsR(magnitude, intR(1))) {
    return power;
  }
  const coefficient = formatStyled(magnitude, style, decimalMark);
  return `${writesAsFraction(magnitude, style) ? `(${coefficient})` : coefficient} × ${power}`;
}
